package com.evoapps.evoshift.ui.user

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.PhotoCamera
import androidx.compose.material.icons.filled.PhotoLibrary
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.filedrop.FileDropArea
import com.evoapps.evoshift.filedrop.FileDropTags
import com.evoapps.evoshift.photo.photoDropSpec
import com.evoapps.evoshift.photo.rememberImagePicker
import evoshift.composeapp.generated.resources.profile_photo_drop
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.ProfilePhotoEvent
import com.evoapps.evoshift.viewmodels.ProfilePhotoViewModel
import com.evoapps.evoshift.widgets.ProfileAvatarSize
import com.evoapps.evoshift.widgets.UserAvatar
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.profile_photo_change
import evoshift.composeapp.generated.resources.profile_photo_choose
import evoshift.composeapp.generated.resources.profile_photo_preparing
import evoshift.composeapp.generated.resources.profile_photo_remove
import evoshift.composeapp.generated.resources.profile_photo_take
import evoshift.composeapp.generated.resources.profile_photo_uploading
import org.jetbrains.compose.resources.stringResource

object ProfilePhotoTags {
    const val AVATAR = "profile_photo_avatar"
    const val PROGRESS = "profile_photo_progress"
}

/**
 * The profile's avatar with its camera button (SH-94): choose a photo from the gallery, take one
 * (where there is a camera), or remove it. A picked photo opens [ProfilePhotoCropDialog]; while
 * it uploads the avatar already shows it, ringed by the upload's progress. Messages go to
 * [snackbarHostState].
 */
@Composable
fun ProfilePhotoEditor(viewModel: ProfilePhotoViewModel, snackbarHostState: SnackbarHostState) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val picker = rememberImagePicker { bytes -> viewModel.onEvent(ProfilePhotoEvent.Picked(bytes)) }
    var menuOpen by remember { mutableStateOf(false) }

    val messageText = state.message?.asString()
    val errorText = state.error?.asString()
    LaunchedEffect(messageText, errorText) {
        (errorText ?: messageText)?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.onEvent(ProfilePhotoEvent.MessageShown)
        }
    }

    val uploading = stringResource(Res.string.profile_photo_uploading)
    val preparing = stringResource(Res.string.profile_photo_preparing)
    // SH-233: a photo dropped on the avatar (web: anywhere on the profile; desktop) goes the gallery's way.
    FileDropArea(
        spec = photoDropSpec,
        dropLabel = stringResource(Res.string.profile_photo_drop),
        onFile = { file -> viewModel.onEvent(ProfilePhotoEvent.Picked(file?.bytes)) },
        enabled = !state.isBusy && state.cropSource == null,
        snackbarHostState = snackbarHostState,
        tag = FileDropTags.PROFILE_PHOTO,
        round = true
    ) {
    Box(contentAlignment = Alignment.Center) {
        UserAvatar(
            name = state.displayName,
            photoUrl = state.photoUrl,
            size = ProfileAvatarSize,
            preview = state.preview,
            modifier = Modifier.testTag(ProfilePhotoTags.AVATAR)
        )
        val progress = state.uploadProgress
        when {
            progress != null -> CircularProgressIndicator(
                progress = { progress },
                modifier = Modifier.size(ProfileAvatarSize + 8.dp).testTag(ProfilePhotoTags.PROGRESS).semantics { contentDescription = uploading },
                strokeWidth = 3.dp
            )
            state.isPreparing || state.isRemoving -> CircularProgressIndicator(
                modifier = Modifier.size(ProfileAvatarSize + 8.dp).testTag(ProfilePhotoTags.PROGRESS).semantics { contentDescription = preparing },
                strokeWidth = 3.dp
            )
        }
        Box(Modifier.align(Alignment.BottomEnd).offset(x = 4.dp, y = 4.dp)) {
            FilledIconButton(
                onClick = { menuOpen = true },
                enabled = !state.isBusy,
                modifier = Modifier.size(36.dp)
            ) {
                Icon(Icons.Default.PhotoCamera, contentDescription = stringResource(Res.string.profile_photo_change), modifier = Modifier.size(20.dp))
            }
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                DropdownMenuItem(
                    text = { Text(stringResource(Res.string.profile_photo_choose)) },
                    leadingIcon = { Icon(Icons.Default.PhotoLibrary, contentDescription = null) },
                    onClick = { menuOpen = false; picker.pickFromGallery() }
                )
                if (picker.canTakePhoto) {
                    DropdownMenuItem(
                        text = { Text(stringResource(Res.string.profile_photo_take)) },
                        leadingIcon = { Icon(Icons.Default.PhotoCamera, contentDescription = null) },
                        onClick = { menuOpen = false; picker.takePhoto() }
                    )
                }
                if (state.hasPhoto) {
                    DropdownMenuItem(
                        text = { Text(stringResource(Res.string.profile_photo_remove), color = MaterialTheme.colorScheme.error) },
                        leadingIcon = { Icon(Icons.Default.Delete, contentDescription = null, tint = MaterialTheme.colorScheme.error) },
                        onClick = { menuOpen = false; viewModel.onEvent(ProfilePhotoEvent.Remove) }
                    )
                }
            }
        }
    }
    }

    state.cropSource?.let { source ->
        ProfilePhotoCropDialog(
            image = source.image,
            onSave = { viewModel.onEvent(ProfilePhotoEvent.CropConfirmed(it)) },
            onCancel = { viewModel.onEvent(ProfilePhotoEvent.CropCancelled) }
        )
    }
}
